import styled, { css } from 'styled-components';
import { LuCheck } from 'react-icons/lu';
import { flow, FlowPanel, Label } from './primitives';

/** The step list in the left column. state: 'done' | 'current' | 'waiting' | 'stopped' | 'todo'. */

export const FLOW_STEPS = ['Select video', 'Upload', 'Analyze', 'Results'];

const List = styled.ol`
	list-style: none;
	margin: 0;
	padding: 0;
	display: flex;
	flex-direction: column;
	gap: 2px;
`;

const tone = {
	current: { bg: flow.greenFill, ring: '#20ac6b' },
	waiting: { bg: 'rgba(224, 160, 48, 0.12)', ring: flow.warn },
	stopped: { bg: 'rgba(226, 90, 74, 0.12)', ring: flow.error },
};

const Item = styled.li`
	display: grid;
	grid-template-columns: 26px 1fr;
	gap: 0.625rem;
	align-items: center;
	padding: 0.55rem 0.5rem;
	border-radius: 10px;
	font-size: 0.85rem;
	color: ${flow.text3};
	${({ $state }) =>
		$state === 'done' &&
		css`
			color: ${flow.text2};
		`}
	${({ $state }) =>
		tone[$state] &&
		css`
			background: ${tone[$state].bg};
			color: ${flow.text};
			font-weight: 600;
		`}
	small {
		display: block;
		font-size: 0.72rem;
		font-weight: 400;
		color: ${flow.text3};
		margin-top: 1px;
		overflow-wrap: anywhere;
	}
`;

const Dot = styled.span`
	width: 22px;
	height: 22px;
	border-radius: 50%;
	display: grid;
	place-items: center;
	font-size: 0.69rem;
	font-weight: 700;
	border: 1.5px solid #3c403c;
	color: ${flow.text3};
	${({ $state, theme }) =>
		$state === 'done' &&
		css`
			background: ${theme.colors.darkGreen};
			border-color: ${theme.colors.darkGreen};
			color: #fff;
		`}
	${({ $state }) =>
		tone[$state] &&
		css`
			border-color: ${tone[$state].ring};
			color: ${tone[$state].ring};
		`}
`;

const STATE_WORD = {
	done: 'done',
	current: 'current step',
	waiting: 'waiting',
	stopped: 'stopped',
};

/**
 * @param current index (0-4) of the active step
 * @param currentState state of the active step ('current' | 'waiting' | 'stopped')
 * @param summaries optional one-line summary per step index
 */
const StepRail = ({
	title = 'New video project',
	current,
	currentState = 'current',
	summaries = {},
}) => (
	<FlowPanel aria-label="Steps">
		<Label as="h2" style={{ margin: 0 }}>
			{title}
		</Label>
		<List>
			{FLOW_STEPS.map((name, i) => {
				const state = i < current ? 'done' : i === current ? currentState : 'todo';
				return (
					<Item
						key={name}
						$state={state}
						aria-current={i === current ? 'step' : undefined}>
						<Dot $state={state} aria-hidden="true">
							{state === 'done' ? (
								<LuCheck size={13} />
							) : state === 'stopped' ? (
								'!'
							) : (
								i + 1
							)}
						</Dot>
						<div>
							{name}
							{STATE_WORD[state] && (
								<span className="visually-hidden">, {STATE_WORD[state]}</span>
							)}
							{summaries[i] && <small>{summaries[i]}</small>}
						</div>
					</Item>
				);
			})}
		</List>
	</FlowPanel>
);

export default StepRail;
